<template>
  <!-- Story-beat timeline, shared by Story-all, Story-focused, and List-detail. Fed enriched beats
       from beatsFor / overallBeatsFor. Quiet affordance icons (metric reading · quote · related
       nodes); the verbatim quote and type labels reveal via the parent's Show-quotes / Show-labels
       toggles. Color stays neutral here (the dot is slate; color is reserved for health); a beat's
       conversation impact reads from a gray directional glyph in the affordance row. -->
  <div>
    <div v-if="!beats.length" class="text-xs text-gray-400">{{ emptyText }}</div>
    <div
      v-for="(b, i) in beats"
      :key="b.engagement != null ? `${b.nodeId != null ? b.nodeId + '-' : ''}${b.engagement}-${i}` : i"
      :data-eng="b.engagement"
      class="relative pl-5 pb-3"
      :class="i < beats.length - 1 ? 'border-l-2 border-gray-200' : ''"
    >
      <!-- dot color = neutral slate; teal ring marks the selected meeting (focused story) -->
      <span
        class="absolute -left-[5px] top-1.5 w-2.5 h-2.5 rounded-full ring-2"
        :class="b.engagement != null && b.engagement === selectedEngagement ? 'ring-accent' : 'ring-white'"
        style="background:#94a3b8"
      ></span>
      <div
        class="rounded-lg px-2 py-1.5 transition-colors"
        :class="b.engagement != null && b.engagement === selectedEngagement ? 'bg-accent-50' : ''"
      >
        <div class="flex items-center gap-2">
          <span class="text-[11px] font-semibold text-gray-500 tabular-nums shrink-0">{{ formatDate(b.date) }}</span>
          <!-- overall story: clickable initiative pill, tinted by the initiative's health -->
          <button
            v-if="showInitiativePill && b.title"
            type="button"
            class="text-[10px] font-semibold px-1.5 py-0.5 rounded-full transition-opacity hover:opacity-80 truncate max-w-[13rem]"
            :style="b.health ? { background: b.health.tint, color: b.health.hex } : {}"
            @click="$emit('open-node', { id: b.nodeId, type: 'initiative' })"
          >{{ b.title }}</button>
          <div class="ml-auto flex items-center gap-1.5 flex-wrap justify-end shrink-0">
            <span
              v-if="b.impact"
              :class="showLabels ? 'inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 text-[10px]' : 'text-gray-300 text-[11px] leading-none'"
              :title="`Conversation impact: ${impactLabel(b.impact)}`"
            >
              <font-awesome-icon :icon="['fas', impactIcon(b.impact)]" />
              <span v-if="showLabels">{{ impactLabel(b.impact) }}</span>
            </span>
            <!-- Sentiment (attitude) glyph — neutral face, the per-beat read the initiative rollup sums. -->
            <span
              v-if="beatSentiment(b)"
              :class="showLabels ? 'inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 text-[10px]' : 'text-gray-300 text-[11px] leading-none'"
              :title="`Sentiment: ${beatSentiment(b).label}`"
            >
              <font-awesome-icon :icon="['fas', beatSentiment(b).icon]" />
              <span v-if="showLabels">{{ beatSentiment(b).label }}</span>
            </span>
            <button
              v-for="chip in b.chips"
              :key="chip.kind"
              type="button"
              :class="showLabels ? 'inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 hover:bg-gray-200 text-[10px]' : 'text-gray-300 hover:text-accent text-[11px] leading-none'"
              :title="chip.kind === 'metric' ? 'Metric reading' : 'Open quote in transcript'"
              @click="onChip(chip, b, $event)"
            >
              <font-awesome-icon :icon="['fas', chip.kind === 'metric' ? 'chart-line' : 'quote-right']" />
              <span v-if="showLabels">{{ chip.kind === 'metric' ? 'Metric' : 'Quote' }}</span>
            </button>
            <span v-if="!showLabels && b.refs && b.refs.length && b.chips && b.chips.length" class="w-px h-3 bg-gray-200"></span>
            <button
              v-for="r in b.refs"
              :key="r.id"
              type="button"
              :class="showLabels ? 'inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 hover:bg-gray-200 text-[10px] max-w-[12rem]' : 'text-gray-300 hover:text-accent text-[11px] leading-none'"
              :title="`${typeLabel(r.type)}: ${r.title}`"
              @click="$emit('open-node', r)"
            >
              <font-awesome-icon :icon="['fas', typeIcon(r.type)]" :class="showLabels ? 'text-gray-400' : ''" />
              <span v-if="showLabels" class="truncate">{{ r.title }}</span>
            </button>
          </div>
        </div>
        <p class="text-sm text-gray-700 leading-relaxed mt-0.5">{{ b.text }}</p>
        <!-- verbatim quote (revealed by the Show-quotes toggle) -->
        <button
          v-if="showQuotes && b.quote"
          type="button"
          class="mt-1.5 w-full text-left rounded-md bg-gray-50 border border-gray-100 px-2.5 py-1.5 hover:border-accent group"
          @click="$emit('open-transcript', b.engagement, b.quote)"
        >
          <p class="text-xs italic text-gray-600 group-hover:text-gray-800 leading-relaxed">"{{ b.quote }}"</p>
          <span v-if="b.quoteSource" class="block mt-0.5 text-[10px] text-gray-400">{{ b.quoteSource }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faChartLine, faQuoteRight, faArrowTrendUp, faArrowTrendDown, faMinus, faBullseye, faBan, faSquareCheck, faComment, faCircleInfo, faFaceSmile, faFaceMeh, faFaceFrown } from "@fortawesome/free-solid-svg-icons";
import { impactFor, typeIcon, typeLabel, fmt, sentimentTagInfo } from "@/components/EngagementTracker/engagementTrackerHelpers.js";

library.add(faChartLine, faQuoteRight, faArrowTrendUp, faArrowTrendDown, faMinus, faBullseye, faBan, faSquareCheck, faComment, faCircleInfo, faFaceSmile, faFaceMeh, faFaceFrown);

export default {
  name: "ET_StoryTimeline",
  components: { FontAwesomeIcon },
  props: {
    // Enriched beats (beatsFor / overallBeatsFor): { date, text, engagement, quote, quoteSource,
    // chips, refs, impact, + nodeId/title/health on overall beats }.
    beats: { type: Array, default: () => [] },
    // Overall story shows a clickable initiative title pill per beat; focused / detail do not.
    showInitiativePill: { type: Boolean, default: false },
    showQuotes: { type: Boolean, default: false },
    showLabels: { type: Boolean, default: false },
    // Highlights the beat whose meeting is selected (focused story ↔ chart marker sync).
    selectedEngagement: { type: [Number, String], default: null },
    emptyText: { type: String, default: "No story yet." },
  },
  emits: ["open-transcript", "open-node", "show-metric"],
  methods: {
    formatDate: fmt.formatDate,
    typeIcon,
    typeLabel,
    impactIcon(v) {
      return (impactFor(v) || {}).icon || "minus";
    },
    impactLabel(v) {
      return (impactFor(v) || {}).label || "";
    },
    beatSentiment(b) {
      return b.sentiment ? sentimentTagInfo(b.sentiment) : null;
    },
    onChip(chip, b, ev) {
      if (chip.kind === "quote") this.$emit("open-transcript", b.engagement, b.quote);
      else this.$emit("show-metric", { node: b.node, engagement: b.engagement, event: ev });
    },
  },
};
</script>
